// RunScene — single scene for all five floors (CLIMB-05). The
// `enterFloor(n)` seam swaps level data while preserving the player and
// pocket across floor transitions. Wave 1B (this plan) populates
// geometry + spawns Player; Wave 2 wires CardPickups + overlap; Wave 3
// wires the cashier transition.

import Phaser from 'phaser';
import { TestSeam } from '../debug/TestSeam';
import { RunState } from '../run/RunState';
import { FLOOR_PRE_FLOP } from '../data/floors/floor-pre-flop';
import { Player } from '../entities/Player';
import { CardPickup } from '../entities/CardPickup';
import { bindPocketBus, PocketSystem } from '../systems/PocketSystem';

interface RunSceneInitData {
  floor?: number;
}

// Visual constants for placeholder geometry rectangles. NOT
// platformer-feel constants — these are only the procedural-texture
// fill colors for solid vs one-way platforms, displayed until Phase 3
// swaps in real tile art.
const SOLID_COLOR = 0x222033;
const ONEWAY_COLOR = 0x3a2a1a;

// One-way platform processCallback margin (px). The collider only
// permits a "land" if the player's bottom is within this margin above
// the platform's top — accommodates a small overlap from the previous
// physics step. Visual / collision tolerance, not a feel constant.
const ONEWAY_TOP_MARGIN_PX = 4;

export class RunScene extends Phaser.Scene {
  // Public for the TestSeam to read (forcePlayerVelocity / teleportPlayer
  // / state snapshot). Cast to `Player` on the consumer side.
  public player?: Player;

  private solidPlatforms?: Phaser.Physics.Arcade.StaticGroup;
  private oneWayPlatforms?: Phaser.Physics.Arcade.StaticGroup;
  private pickupGroup?: Phaser.GameObjects.Group;

  constructor() {
    super('RunScene');
  }

  create(data?: RunSceneInitData): void {
    TestSeam.setScene('RunScene');

    // Bind PocketSystem to this scene's event bus BEFORE enterFloor so
    // any tryAdd happening during initial spawn (none today, but Plan 05+
    // may pre-deal cards) emits on the live bus rather than a dropped null.
    bindPocketBus(this.events);

    this.scene.launch('RunUIScene', { runScene: this });

    // Camera + world bounds set from the active floor's dimensions.
    // Wave 1 ships only one floor; later phases swap the const.
    this.cameras.main.setBounds(0, 0, FLOOR_PRE_FLOP.width, FLOOR_PRE_FLOP.height);
    this.physics.world.setBounds(0, 0, FLOOR_PRE_FLOP.width, FLOOR_PRE_FLOP.height);
    this.cameras.main.setBackgroundColor(FLOOR_PRE_FLOP.bgColor);

    this.enterFloor(data?.floor ?? 0);
  }

  enterFloor(n: number): void {
    // Tear down any prior floor's groups (idempotent — safe to call
    // even on first entry where groups are undefined).
    this.solidPlatforms?.clear(true, true);
    this.oneWayPlatforms?.clear(true, true);
    this.pickupGroup?.clear(true, true);
    this.solidPlatforms = this.physics.add.staticGroup();
    this.oneWayPlatforms = this.physics.add.staticGroup();
    this.pickupGroup = this.add.group();

    // Phase 1 only ever enters floor 0. The const lookup grows to a
    // table indexed by `n` in Phase 3 once five floors exist.
    const floor = FLOOR_PRE_FLOP;

    for (const rect of floor.geometry) {
      const tex = this.makeRectTexture(
        rect.w,
        rect.h,
        rect.oneWay ? ONEWAY_COLOR : SOLID_COLOR,
      );
      const group = rect.oneWay ? this.oneWayPlatforms : this.solidPlatforms;
      const sprite = group.create(
        rect.x + rect.w / 2,
        rect.y + rect.h / 2,
        tex,
      ) as Phaser.Physics.Arcade.Sprite;
      sprite.setOrigin(0.5);
    }

    const playerSpawn = floor.spawns.find((s) => s.type === 'player');
    if (!playerSpawn) {
      throw new Error('floor-pre-flop missing player spawn');
    }
    this.player = new Player(this, playerSpawn.x, playerSpawn.y);

    this.physics.add.collider(this.player, this.solidPlatforms);

    // One-way platforms via processCallback (RESEARCH Pattern 7).
    // Returns true if the collision should proceed:
    //   1. drop-through window inactive (player.dropThroughUntilMs <= now)
    //   2. player is descending OR stationary (velocity.y >= 0)
    //   3. player's bottom is at or above the platform's top (with margin)
    this.physics.add.collider(
      this.player,
      this.oneWayPlatforms,
      undefined,
      (p, plat) => {
        const player = p as Player;
        const playerBody = player.body as Phaser.Physics.Arcade.Body;
        const platBody = (plat as Phaser.Physics.Arcade.Sprite)
          .body as Phaser.Physics.Arcade.StaticBody;
        if (player.dropThroughUntilMs > this.time.now) return false;
        return (
          playerBody.velocity.y >= 0 &&
          playerBody.bottom <= platBody.top + ONEWAY_TOP_MARGIN_PX
        );
      },
      this,
    );

    // Card pickups — spawn one CardPickup per `card` spawn from the
    // floor data and add to pickupGroup. Each is its own GameObject
    // with an Arcade body sized to the visual; physics.add.overlap
    // takes the group and dispatches per-pickup overlap callbacks.
    for (const spawn of floor.spawns) {
      if (spawn.type !== 'card' || !spawn.card) continue;
      const pickup = new CardPickup(this, spawn.x, spawn.y, spawn.card);
      this.pickupGroup.add(pickup);
    }

    this.physics.add.overlap(
      this.player,
      this.pickupGroup,
      this.onPlayerPickupOverlap,
      undefined,
      this,
    );

    RunState.currentFloor = n;
    this.events.emit('floor:entered', n);

    // Cashier transition keys (D-07 debug keybind). Bound here in
    // enterFloor (rather than create) because keyboard listeners belong
    // with the gameplay surface they affect. Phaser dedupes identical
    // listener registrations, but enterFloor today only runs once per
    // RunScene lifetime in Phase 1 — rebinding on re-entry is a Phase 3
    // concern (we'll switch to off-then-on if floors stop reusing scenes).
    this.input.keyboard!.on('keydown-C', () => this.tryEnterCashier());
    this.input.keyboard!.on('keydown-ENTER', () => this.tryEnterCashier());
  }

  // Public so the test seam (`__TEST__.commands.forceCashierLock`, Plan 05
  // Task 5.3) can invoke the SAME code path as the keyboard binding (D-18).
  // D-17: silent no-op when pocket has fewer than 5 cards.
  // RunUIScene must be explicitly stopped because it's launched in
  // parallel via scene.launch — Phaser only auto-stops the scene that
  // calls scene.start(...), not its parallel siblings.
  public tryEnterCashier(): void {
    if (RunState.pocket.length < 5) return;
    this.scene.stop('RunUIScene');
    this.scene.start('CashierScene');
  }

  // Overlap callback: gates on pickup.active so a pickup destroyed in a
  // prior overlap step within the same frame doesn't fire twice; calls
  // PocketSystem.tryAdd; on success destroys the pickup immediately so
  // it cannot re-trigger overlap on subsequent steps (T-01-09 / PITFALL #7
  // — audience-fatal duplicate-pickup mitigation). On capacity-failure
  // the pickup remains; Phase 5 polish adds a visible "denied" feedback.
  //
  // Signature uses Phaser's `ArcadePhysicsCallback` union shape (object
  // can be a Body/StaticBody/Tile/GameObjectWithBody). For our overlap
  // setup (player vs CardPickup group) the second argument is always a
  // CardPickup at runtime; we narrow the cast and check `.active` to
  // satisfy the shape.
  private onPlayerPickupOverlap: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = (
    _player,
    pickupObj,
  ) => {
    const pickup = pickupObj as CardPickup;
    if (!pickup.active) return;
    if (PocketSystem.tryAdd(pickup.card)) {
      pickup.destroy();
    }
  };

  override update(time: number, delta: number): void {
    // Phaser's Scene base does NOT auto-update child sprites unless they
    // are added to the scene's update list explicitly. We call player
    // update manually so ordering is deterministic.
    this.player?.update(time, delta);
  }

  // Cache key per (w, h, color) triplet — re-used across identical rects
  // so we generate each visual only once.
  private makeRectTexture(w: number, h: number, color: number): string {
    const key = `plat:${w}x${h}:0x${color.toString(16)}`;
    if (this.textures.exists(key)) return key;
    const g = this.add.graphics({ x: 0, y: 0 });
    g.fillStyle(color, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(key, w, h);
    g.destroy();
    return key;
  }
}
